import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    AutoAwesomeRounded,
    CloseRounded,
    DeleteOutlineRounded,
    DriveFileRenameOutlineRounded,
    EditRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import ObjectIcon, { OBJECT_ICONS } from "../components/ObjectIcon";
import { Button, IconButton } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import {
    applySalesCrmTemplate,
    createMetaField,
    createMetaObject,
    deleteMetaField,
    deleteMetaObject,
    updateMetaField,
    updateMetaObject,
} from "../api";
import {
    FIELD_TYPES,
    TEXTISH_TYPES,
    apiError,
    fieldTypeLabel,
} from "../utils/meta";

const LABEL_SX = {
    fontSize: 11,
    letterSpacing: "2px",
    fontWeight: 700,
    color: "var(--text-tertiary)",
};

/* ------------------------------------------------------------------ */
/* Icon picker — shared by the new-object and rename dialogs             */
/* ------------------------------------------------------------------ */

function IconPicker({ value, onChange }) {
    return (
        <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>ICON</Typography>
            <Stack
                sx={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                {OBJECT_ICONS.map((key) => (
                    <Stack
                        key={key}
                        onClick={() => onChange(key)}
                        sx={{
                            width: 40,
                            height: 40,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "3px",
                            cursor: "pointer",
                            border:
                                value === key
                                    ? "1.5px solid var(--text)"
                                    : "1px solid var(--border-mid)",
                            background:
                                value === key
                                    ? "var(--bg-tint-soft)"
                                    : "transparent",
                            color: "var(--text)",
                        }}
                    >
                        <ObjectIcon name={key} size={18} />
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* New object dialog — name + icon picker                               */
/* ------------------------------------------------------------------ */

function NewObjectDialog({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState(OBJECT_ICONS[0]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName("");
            setIcon(OBJECT_ICONS[0]);
        }
    }, [open]);

    async function handleCreate() {
        if (!name.trim()) {
            toast.error("Give the object a name");
            return;
        }
        setSaving(true);
        try {
            const { data } = await createMetaObject({
                name: name.trim(),
                icon,
            });
            toast.success(`Object "${data?.name ?? name.trim()}" created`);
            onCreated(data);
        } catch (err) {
            toast.error(apiError(err, "Could not create the object"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                New object
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2.5, pt: 1 }}>
                    <TextField
                        label="Name"
                        placeholder="e.g. Properties, Candidates, Shipments"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <IconPicker value={icon} onChange={setIcon} />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={saving}
                    onClick={handleCreate}
                >
                    Create object
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Rename dialog — name + icon, never the slug                          */
/* ------------------------------------------------------------------ */

function RenameObjectDialog({ open, object, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState(OBJECT_ICONS[0]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(object?.name ?? "");
        setIcon(object?.icon ?? OBJECT_ICONS[0]);
    }, [open, object]);

    async function handleSave() {
        if (!name.trim()) {
            toast.error("Give the object a name");
            return;
        }
        setSaving(true);
        try {
            const body = { name: name.trim(), icon };
            await updateMetaObject(object.id, body);
            toast.success(`Object renamed to "${body.name}"`);
            await onSaved(body);
        } catch (err) {
            toast.error(apiError(err, "Could not rename the object"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Rename object
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2.5, pt: 1 }}>
                    <TextField
                        label="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <IconPicker value={icon} onChange={setIcon} />
                    <Typography
                        sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}
                    >
                        The API slug stays{" "}
                        <Typography
                            component="code"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: 12.5,
                                color: "var(--text-secondary)",
                            }}
                        >
                            {object?.slug}
                        </Typography>{" "}
                        so existing integrations keep working.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={saving}
                    onClick={handleSave}
                >
                    Save changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Add / edit field dialog                                              */
/* ------------------------------------------------------------------ */

function FieldDialog({ open, field, currentObject, onClose, onSave, saving }) {
    const { objects } = useObjects();
    const editing = !!field;
    const [label, setLabel] = useState("");
    const [type, setType] = useState("text");
    const [required, setRequired] = useState(false);
    const [options, setOptions] = useState([]);
    const [optionDraft, setOptionDraft] = useState("");
    const [relObjectId, setRelObjectId] = useState("");
    const [relDisplayField, setRelDisplayField] = useState("");

    useEffect(() => {
        if (!open) return;
        setLabel(field?.label ?? "");
        setType(field?.type ?? "text");
        setRequired(!!field?.required);
        setOptions(field?.config?.options ?? []);
        setOptionDraft("");
        setRelObjectId(field?.config?.objectId ?? "");
        setRelDisplayField(field?.config?.displayField ?? "");
    }, [open, field]);

    /* Other objects first; a self-reference is legal, so the current object
       stays on the list — last, and named for what it is. */
    const relationTargets = useMemo(() => {
        const others = objects.filter((o) => o.id !== currentObject?.id);
        const self = objects.find((o) => o.id === currentObject?.id);
        return [
            ...others.map((o) => ({ id: o.id, name: o.name, icon: o.icon })),
            ...(self
                ? [
                      {
                          id: self.id,
                          name: `${self.name} (this object)`,
                          icon: self.icon,
                      },
                  ]
                : []),
        ];
    }, [objects, currentObject?.id]);

    /* The label candidates on the target — name-like fields lead. */
    const displayFieldOptions = useMemo(() => {
        const target = objects.find((o) => o.id === relObjectId);
        const fields = [...(target?.fields ?? [])].sort(
            (a, b) => (a.position ?? 0) - (b.position ?? 0),
        );
        const textish = fields.filter((f) => TEXTISH_TYPES.includes(f.type));
        const rest = fields.filter((f) => !TEXTISH_TYPES.includes(f.type));
        return [...textish, ...rest];
    }, [objects, relObjectId]);

    function addOption() {
        const v = optionDraft.trim();
        if (!v || options.includes(v)) return;
        setOptions((prev) => [...prev, v]);
        setOptionDraft("");
    }

    function handleSave() {
        if (!label.trim()) {
            toast.error("Give the field a label");
            return;
        }
        if (type === "picklist" && options.length === 0) {
            toast.error("Add at least one picklist option");
            return;
        }
        if (type === "relation" && !relObjectId) {
            toast.error("Pick the object this field links to");
            return;
        }
        const body = { label: label.trim(), required };
        if (!editing) body.type = type;
        if (type === "picklist") body.config = { options };
        if (type === "relation") {
            body.config = {
                objectId: relObjectId,
                ...(relDisplayField ? { displayField: relDisplayField } : {}),
            };
        }
        onSave(body);
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                {editing ? "Edit field" : "Add a field"}
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2.5, pt: 1 }}>
                    <TextField
                        label="Label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Type"
                        select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        disabled={editing}
                        helperText={
                            editing
                                ? "A field's type can't change once created"
                                : undefined
                        }
                        fullWidth
                    >
                        {FIELD_TYPES.map((t) => (
                            <MenuItem key={t.key} value={t.key}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    {type === "picklist" && (
                        <Stack sx={{ gap: 1 }}>
                            <Typography sx={LABEL_SX}>OPTIONS</Typography>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                }}
                            >
                                {options.map((opt) => (
                                    <Chip
                                        key={opt}
                                        size="small"
                                        label={opt}
                                        onDelete={() =>
                                            setOptions((prev) =>
                                                prev.filter((o) => o !== opt),
                                            )
                                        }
                                        sx={{
                                            background: "var(--bg-tint-soft)",
                                            border: "1px solid var(--border-mid)",
                                        }}
                                    />
                                ))}
                                {options.length === 0 && (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: "var(--text-tertiary)",
                                        }}
                                    >
                                        No options yet
                                    </Typography>
                                )}
                            </Stack>
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    placeholder="Add an option…"
                                    value={optionDraft}
                                    onChange={(e) =>
                                        setOptionDraft(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addOption();
                                        }
                                    }}
                                    size="small"
                                    fullWidth
                                />
                                <Button
                                    variant="soft-outlined"
                                    onClick={addOption}
                                    sx={{ height: 40, flexShrink: 0 }}
                                >
                                    Add
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {type === "relation" && (
                        <Stack sx={{ gap: 2.5 }}>
                            <TextField
                                label="Links to"
                                select
                                required
                                fullWidth
                                value={relObjectId}
                                onChange={(e) => {
                                    setRelObjectId(e.target.value);
                                    setRelDisplayField("");
                                }}
                                helperText="The object whose records this field points at"
                            >
                                {relationTargets.length === 0 && (
                                    <MenuItem value="" disabled>
                                        No other objects yet
                                    </MenuItem>
                                )}
                                {relationTargets.map((o) => (
                                    <MenuItem key={o.id} value={o.id}>
                                        <Stack
                                            component="span"
                                            sx={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <ObjectIcon
                                                name={o.icon}
                                                size={15}
                                            />
                                            {o.name}
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Show which field"
                                select
                                fullWidth
                                value={relDisplayField}
                                onChange={(e) =>
                                    setRelDisplayField(e.target.value)
                                }
                                disabled={!relObjectId}
                                helperText="Used as the label when showing linked records"
                            >
                                <MenuItem value="">
                                    First filled field
                                </MenuItem>
                                {displayFieldOptions.map((f) => (
                                    <MenuItem key={f.key} value={f.key}>
                                        {f.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    )}

                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Stack>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                Required
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    color: "var(--text-secondary)",
                                }}
                            >
                                Records can't be saved without a value
                            </Typography>
                        </Stack>
                        <Switch
                            checked={required}
                            onChange={(e) => setRequired(e.target.checked)}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={saving}
                    onClick={handleSave}
                >
                    {editing ? "Save field" : "Add field"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Field manager drawer                                                 */
/* ------------------------------------------------------------------ */

function FieldManager({ object, onClose, onChanged }) {
    const open = !!object;
    const [fieldDialog, setFieldDialog] = useState({
        open: false,
        field: null,
    });
    const [fieldSaving, setFieldSaving] = useState(false);
    const [deletingField, setDeletingField] = useState(null);
    const [busy, setBusy] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    /* Locally applied name/icon so the header updates before the refetch. */
    const [renamed, setRenamed] = useState(null);

    const fields = useMemo(
        () =>
            [...(object?.fields ?? [])].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
            ),
        [object],
    );

    /* The object as the drawer shows it — never changes the slug. */
    const shown = object
        ? {
              ...object,
              ...(renamed?.id === object.id ? renamed.patch : {}),
          }
        : null;

    useEffect(() => {
        if (!open) setRenamed(null);
    }, [open]);

    async function saveField(body) {
        setFieldSaving(true);
        try {
            if (fieldDialog.field) {
                await updateMetaField(fieldDialog.field.id, body);
                toast.success(`Field "${body.label}" updated`);
            } else {
                await createMetaField(object.id, body);
                toast.success(`Field "${body.label}" added`);
            }
            setFieldDialog({ open: false, field: null });
            await onChanged();
        } catch (err) {
            toast.error(apiError(err, "Could not save the field"));
        } finally {
            setFieldSaving(false);
        }
    }

    async function removeField() {
        if (!deletingField) return;
        setBusy(true);
        try {
            await deleteMetaField(deletingField.id);
            toast.success(`Field "${deletingField.label}" deleted`);
            setDeletingField(null);
            await onChanged();
        } catch (err) {
            toast.error(apiError(err, "Could not delete the field"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 680 }, background: "var(--bg)" },
            }}
        >
            {shown && (
                <Stack sx={{ height: "100%" }}>
                    <Stack
                        sx={{
                            p: "24px 28px 20px",
                            borderBottom: "1px solid var(--border)",
                            gap: 0.5,
                            background: "var(--bg-secondary)",
                        }}
                    >
                        <Typography sx={LABEL_SX}>OBJECT BUILDER</Typography>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1.25,
                            }}
                        >
                            <ObjectIcon name={shown.icon} size={20} />
                            <Typography variant="h5">{shown.name}</Typography>
                            <Chip
                                size="small"
                                label={shown.slug}
                                sx={{
                                    background: "var(--bg-tint-soft)",
                                    border: "1px solid var(--border-mid)",
                                    color: "var(--text-secondary)",
                                    fontFamily: "monospace",
                                    fontSize: 11.5,
                                }}
                            />
                            <Button
                                variant="text"
                                startIcon={
                                    <DriveFileRenameOutlineRounded
                                        sx={{ fontSize: 16 }}
                                    />
                                }
                                onClick={() => setRenameOpen(true)}
                                sx={{ height: 30, fontSize: 13, px: 1 }}
                            >
                                Rename
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack
                        sx={{ flex: 1, overflowY: "auto", p: "24px 28px", gap: 2 }}
                    >
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography sx={LABEL_SX}>FIELDS</Typography>
                            <Button
                                variant="soft-outlined"
                                startIcon={<AddRounded />}
                                sx={{ height: 36 }}
                                onClick={() =>
                                    setFieldDialog({ open: true, field: null })
                                }
                            >
                                Add field
                            </Button>
                        </Stack>

                        <TableContainer
                            sx={{ background: "var(--bg-secondary)" }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Label</TableCell>
                                        <TableCell>Key</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Required</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fields.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13.5,
                                                        color: "var(--text-secondary)",
                                                        py: 2,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    No fields yet — add the
                                                    first one
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {fields.map((f) => (
                                        <TableRow key={f.id}>
                                            <TableCell
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {f.label}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily: "monospace",
                                                    fontSize: 12.5,
                                                    color: "var(--text-secondary)",
                                                }}
                                            >
                                                {f.key}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={fieldTypeLabel(f)}
                                                    sx={{
                                                        background:
                                                            "var(--bg-tint-soft)",
                                                        border: "1px solid var(--border-mid)",
                                                        color: "var(--text-secondary)",
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {f.required ? "Yes" : "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack
                                                    sx={{
                                                        flexDirection: "row",
                                                        justifyContent:
                                                            "flex-end",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <IconButton
                                                        size="30px"
                                                        variant="text"
                                                        title="Edit"
                                                        onClick={() =>
                                                            setFieldDialog({
                                                                open: true,
                                                                field: f,
                                                            })
                                                        }
                                                    >
                                                        <EditRounded
                                                            sx={{
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        size="30px"
                                                        variant="text"
                                                        color="error"
                                                        title="Delete"
                                                        onClick={() =>
                                                            setDeletingField(f)
                                                        }
                                                    >
                                                        <DeleteOutlineRounded
                                                            sx={{
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>

                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            p: "16px 28px",
                            borderTop: "1px solid var(--border)",
                            background: "var(--bg-secondary)",
                        }}
                    >
                        <Button
                            variant="soft-outlined"
                            startIcon={<CloseRounded />}
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </Stack>
                </Stack>
            )}

            <RenameObjectDialog
                open={renameOpen}
                object={shown}
                onClose={() => setRenameOpen(false)}
                onSaved={async (patch) => {
                    setRenamed({ id: object.id, patch });
                    setRenameOpen(false);
                    await onChanged();
                }}
            />

            <FieldDialog
                open={fieldDialog.open}
                field={fieldDialog.field}
                currentObject={object}
                saving={fieldSaving}
                onClose={() => setFieldDialog({ open: false, field: null })}
                onSave={saveField}
            />

            <Dialog
                open={!!deletingField}
                onClose={() => setDeletingField(null)}
            >
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this field?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{deletingField?.label}&#8221; and its values on
                        existing records will be removed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeletingField(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={busy}
                        onClick={removeField}
                    >
                        Delete field
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminObjects() {
    const { objects, loading, refresh } = useObjects();
    const [newOpen, setNewOpen] = useState(false);
    const [managingId, setManagingId] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [templateBusy, setTemplateBusy] = useState(false);

    /* Keep the drawer's object fresh across refreshes. */
    const managing = objects.find((o) => o.id === managingId) ?? null;

    async function toggleNav(row, showInNav) {
        try {
            await updateMetaObject(row.id, { showInNav });
            await refresh();
        } catch (err) {
            toast.error(apiError(err, "Could not update the object"));
        }
    }

    async function handleDelete() {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await deleteMetaObject(deleting.id);
            toast.success(`Object "${deleting.name}" deleted`);
            setDeleting(null);
            await refresh();
        } catch (err) {
            toast.error(
                apiError(
                    err,
                    "Could not delete the object — it may still have records",
                ),
            );
        } finally {
            setDeleteBusy(false);
        }
    }

    async function useTemplate() {
        setTemplateBusy(true);
        try {
            const { data } = await applySalesCrmTemplate();
            toast.success(
                `Created ${data?.created?.length ?? 4} objects from the Sales CRM template`,
            );
            await refresh();
        } catch (err) {
            toast.error(apiError(err, "Could not apply the template"));
        } finally {
            setTemplateBusy(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Object",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1.25,
                        }}
                    >
                        <ObjectIcon name={row.icon} size={17} />
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {row.name}
                        </Typography>
                    </Stack>
                ),
            },
            {
                key: "slug",
                label: "Slug",
                render: (row) => (
                    <Typography
                        sx={{
                            fontFamily: "monospace",
                            fontSize: 12.5,
                            color: "var(--text-secondary)",
                        }}
                    >
                        {row.slug}
                    </Typography>
                ),
            },
            {
                key: "fields",
                label: "Fields",
                align: "right",
                render: (row) => row.fields?.length ?? 0,
            },
            {
                key: "recordCount",
                label: "Records",
                align: "right",
                render: (row) => row.recordCount ?? 0,
            },
            {
                key: "showInNav",
                label: "In nav",
                render: (row) => (
                    <Switch
                        size="small"
                        checked={row.showInNav !== false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => toggleNav(row, e.target.checked)}
                    />
                ),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Manage fields"
                            onClick={() => setManagingId(row.id)}
                        >
                            <EditRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            color="error"
                            title="Delete"
                            onClick={() => setDeleting(row)}
                        >
                            <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Objects"
                subtitle="Define what your workspace tracks — every object is yours"
                action={
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        {objects.length === 0 && !loading && (
                            <Button
                                variant="soft-outlined"
                                startIcon={<AutoAwesomeRounded />}
                                loading={templateBusy}
                                onClick={useTemplate}
                            >
                                Use Sales CRM template
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddRounded />}
                            onClick={() => setNewOpen(true)}
                        >
                            New object
                        </Button>
                    </Stack>
                }
            />

            <DataTable
                columns={columns}
                rows={objects}
                loading={loading}
                emptyMessage="No objects yet — create one or start from the Sales CRM template"
                onRowClick={(row) => setManagingId(row.id)}
            />

            {objects.length > 0 && (
                <Typography
                    sx={{
                        mt: 2,
                        fontSize: 13,
                        color: "var(--text-tertiary)",
                    }}
                >
                    Records live at{" "}
                    <Typography
                        component={Link}
                        to={`/o/${objects[0].slug}`}
                        sx={{
                            fontSize: 13,
                            color: "var(--accent)",
                            fontWeight: 600,
                            textDecoration: "none",
                        }}
                    >
                        /o/&lt;slug&gt;
                    </Typography>{" "}
                    — each object gets a list, a board and a pipeline builder.
                </Typography>
            )}

            <NewObjectDialog
                open={newOpen}
                onClose={() => setNewOpen(false)}
                onCreated={async (obj) => {
                    setNewOpen(false);
                    await refresh();
                    if (obj?.id) setManagingId(obj.id);
                }}
            />

            <FieldManager
                object={managing}
                onClose={() => setManagingId(null)}
                onChanged={refresh}
            />

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this object?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{deleting?.name}&#8221; will be removed along
                        with its fields and pipelines. Objects that still hold
                        records can't be deleted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeleting(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={deleteBusy}
                        onClick={handleDelete}
                    >
                        Delete object
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
